// @ts-strict-ignore
import { ICreateArticleOperation } from './i-create-article-operation';
import { Dictionary } from 'typescript-collections';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { IOperation } from '@shared/undo/operation/i-operation';
import { Vector3 } from 'three';
import { ArticleVO } from '@article/model/vo/article.vo';
import { SuitableConnectionsVO } from '../../model/vo/suitable-connections.vo';
import { IArticleConnectProcessor } from './connect/i-article-connect.processor';
import { ArticleContainersWrapper } from '@3d-scene/model3d/article-containers-wrapper';
import { ArticleConnectProcessorRegistry } from './connect/article-connect-processor-registry';
import { ConnectArticlePrepareData } from './insert/connect-article-prepare-data';
import { ConnectionCubeSearchHelper } from '@article/components/connections/connection-creation/search/connection-cube-search.helper';
import { ArrayUtils } from '@shared/util/array-utils';
import { ConnectionAreaVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { LabelingArticleConnectProcessor } from './connect/labeling-article-connect.processor';
import { DynamicLabelingArticleConnectProcessor } from './connect/dynamic-labeling-article-connect.processor';
import { Log } from '@shared/debug/log';

export class ConnectArticleOperation implements ICreateArticleOperation {

  private _articleToCreate: ArticleVO;
  private _connectPrepareDatas: ConnectArticlePrepareData[];
  private _suitableGivingConnections: SuitableConnectionsVO;

  private _usageIdsToDetach: string[];
  private _connectArticleProcessor: IArticleConnectProcessor;

  private _articleWrappers: ArticleContainersWrapper[];
  private _loadingArticleWrappers: ArticleContainersWrapper[];
  private _createdUndoOperations: IOperation[] = null;

  private _errorMessage: string;


  constructor(
    private _isUnusedLabeling: boolean,
    private connectProcessorRegistry: ArticleConnectProcessorRegistry,
    private connectionCubeSearchHelper: ConnectionCubeSearchHelper
  ) { }

  public get usageIdsToDetach(): string[] {
    return this._usageIdsToDetach;
  }

  public get moveArticlesMap(): Dictionary<string, Vector3> {
    return null;
  }

  public get createdArticleContainers(): ArticleContainer[] {
    if (ArrayUtils.isEmpty(this._articleWrappers)) {
      return [];
    }
    let containers: ArticleContainer[] = [];
    for (const wrapper of this._articleWrappers) {
      containers = containers.concat(wrapper.articleContainers);
    }
    return containers;
  }

  public get createdUndoOperations(): IOperation[] {
    return this._createdUndoOperations;
  }

  public async execute(articleToCreate: ArticleVO, connectPrepareDatas: ConnectArticlePrepareData[],
    suitableGivingConnections: SuitableConnectionsVO, usageIdsToDetach: string[] = null): Promise<{removeSelection:boolean}> {
    if (connectPrepareDatas.length > 0) {
      this._articleToCreate = articleToCreate;
      this._connectPrepareDatas = connectPrepareDatas;
      this._suitableGivingConnections = suitableGivingConnections;
      this._usageIdsToDetach = usageIdsToDetach;
      const connectionSetId: string = this.connectionCubeSearchHelper.getConnectionSetIdBySuitableSet(
        this._suitableGivingConnections.connectionSets[0],
        !articleToCreate.baseArticle ? articleToCreate : articleToCreate.baseArticle
      );
      this._connectArticleProcessor = this.connectProcessorRegistry.get(this._articleToCreate, connectionSetId, this._isUnusedLabeling);
      return this.prepareConnectOperation();
    } else {
      return this.finalizeConnectSuccessOperation();
    }
  }

  private async prepareConnectOperation(): Promise<{removeSelection:boolean}> {
    try {
     const preProcessData: {wrappers: ArticleContainersWrapper[], usageIdsToDetach: string[]} = await new Promise(resolve=>{
        this._connectArticleProcessor.preProcess(this._articleToCreate, this._connectPrepareDatas,
          (wrappers: ArticleContainersWrapper[], usageIdsToDetach: string[]) => resolve({wrappers, usageIdsToDetach}),
          this._usageIdsToDetach);
        })
       return this.preProcessHandlerFunction(preProcessData.wrappers, preProcessData.usageIdsToDetach)
    } catch (error) {
      this.finalizeConnectFailureOperation();
      throw error;
    }
  }

  private async preProcessHandlerFunction(wrappers: ArticleContainersWrapper[], usageIdsToDetach: string[] = null): Promise<{removeSelection:boolean}> {
    if (ArrayUtils.isEmpty(wrappers)) {
      return this.finalizeConnectSuccessOperation(false);
    } else {
      return this.preProcess(wrappers, usageIdsToDetach);
    }
  }

  private async preProcess(wrappers: ArticleContainersWrapper[], usageIdsToDetach: string[] = null): Promise<{removeSelection:boolean}> {
    this._articleWrappers = wrappers.concat();
    let removeSelection: Promise<{ removeSelection: boolean }>// = Promise.reject({removeSelection:false});
    this._loadingArticleWrappers = this._articleWrappers.concat();
    if (!this._usageIdsToDetach) {
      this._usageIdsToDetach = [];
    }
    ArrayUtils.mergeArrays(this._usageIdsToDetach, usageIdsToDetach);

    for (const articlesWrapper of wrappers) {
      let wrapperCreated = true;
      await articlesWrapper.refreshWrapper(!this._isUnusedLabeling)
      .catch((error) => {
Log.error('Could not refresh article wrapper: ', error); wrapperCreated = false
});

      if (wrapperCreated) {
        removeSelection = this.articleContainerCreated(articlesWrapper.wrapperGUID);
      }
    }
    return removeSelection;
  }

  private async articleContainerCreated(guid: string): Promise<{removeSelection:boolean}> {
    const loadingArticleWrapper: ArticleContainersWrapper = this.findLoadingContainerByGUID(guid);
    if (!loadingArticleWrapper) {
      return;
    }
    this._loadingArticleWrappers.splice(this._loadingArticleWrappers.indexOf(loadingArticleWrapper), 1);

    try {
      const givingAreas: ConnectionAreaVO[] = this.findGivingAreasByWrapper(loadingArticleWrapper);
      this._connectArticleProcessor.clear();
      this._connectArticleProcessor.process(loadingArticleWrapper.articleContainers, loadingArticleWrapper.baseArticleContainer, givingAreas, NaN);
      if (this._isUnusedLabeling) {
        if (this._loadingArticleWrappers.length === 0) {
          return this.finalizeConnectSuccessOperation(false);
        }
      } else {
        this.storeCreatedUndoOperations();
        if (this._loadingArticleWrappers.length === 0) {
          this.postProcessArticles();
          const removeSelection: boolean = !(
            this._connectArticleProcessor instanceof LabelingArticleConnectProcessor ||
            this._connectArticleProcessor instanceof DynamicLabelingArticleConnectProcessor
          );
          return this.finalizeConnectSuccessOperation(removeSelection);
        }
      }
    } catch (error) {
      this._articleWrappers.splice(this._articleWrappers.indexOf(loadingArticleWrapper), 1);
      if (loadingArticleWrapper) {
        loadingArticleWrapper.disposeArticleContainers();
      }
      if (!this._errorMessage) {
        this._errorMessage = error.message;
      }
      if (this._loadingArticleWrappers.length === 0) {
        if (this._articleWrappers.length > 0) {
          return this.finalizeConnectSuccessOperation();
        } else {
          this.finalizeConnectFailureOperation();
          throw new Error(this._errorMessage);
        }
      }
    }
  }

  private findGivingAreasByWrapper(articleWrapper: ArticleContainersWrapper): ConnectionAreaVO[] {
    if (!this._articleToCreate || !articleWrapper || !articleWrapper.givingConnectionNames) {
      return [];
    }
    const articleToFindAreas: ArticleVO = !this._articleToCreate.baseArticle ? this._articleToCreate : this._articleToCreate.baseArticle;
    const connectionAreas: ConnectionAreaVO[] = [];
    for (const connectionName of articleWrapper.givingConnectionNames) {
      const connectionArea: MetaElementVO = ArticleUtils.findMetaElementByName(articleToFindAreas, connectionName);
      if (connectionArea && connectionArea instanceof ConnectionAreaVO) {
        connectionAreas.push(connectionArea);
      }
    }
    return connectionAreas;
  }

  private postProcessArticles(): void {
    this._connectArticleProcessor.postProcess(this.createdArticleContainers);
  }

  private storeCreatedUndoOperations(): void {
    if (!this._connectArticleProcessor || !this._connectArticleProcessor.createdUndoOperations) {
      return;
    }
    if (!this._createdUndoOperations) {
      this._createdUndoOperations = [];
    }
    this._createdUndoOperations = this._createdUndoOperations.concat(this._connectArticleProcessor.createdUndoOperations);
  }

  private finalizeConnectSuccessOperation(removeSelection: boolean = true): {removeSelection:boolean} {
    if (this._connectArticleProcessor) {
      this._connectArticleProcessor?.clear();
    }
    return {removeSelection};
  }

  private finalizeConnectFailureOperation(): void {
    this._connectArticleProcessor?.clear();
  }

  private findLoadingContainerByGUID(guid: string): ArticleContainersWrapper {
    for (const wrapper of this._loadingArticleWrappers) {
      if (guid === wrapper.wrapperGUID) {
        return wrapper;
      }
    }
    return null;
  }

  public clear(): void {
    this._articleToCreate = null;
    this._connectPrepareDatas = null;
    this._suitableGivingConnections = null;
    this._usageIdsToDetach = null;
    this._connectArticleProcessor = null;
    this._articleWrappers = null;
    this._loadingArticleWrappers = null;
    this._createdUndoOperations = null;
    this._errorMessage = null;
  }
}
